import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  InputAdornment,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Phone,
  LocationOn,
  LocationCity,
  Public,
  Person,
  Close,
  Visibility,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { BASE_URL } from "../components/config/config";

const WarrantyRegistrationForm = ({
  formData,
  setFormData,
  handleChange,
  clientId,
  onSubmit,
}) => {
  const [file, setFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    otp: "",
    verifyOtp: false,
  });
  const [resendTimer, setResendTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isLoading, setIsLoading] = useState({
    sendingOtp: false,
    verifyingOtp: false,
    submitting: false,
  });

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else if (resendTimer === 0) {
      setCanResendOtp(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const validateForm = () => {
    if (
      !formData.name ||
      !formData.phone ||
      !formData.pincode ||
      !formData.city ||
      !formData.state
    ) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return false;
    }

    if (formData.phone.length !== 10) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 10-digit phone number",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!file) {
      setSnackbar({
        open: true,
        message: "Please upload warranty document",
        severity: "error",
      });
      return;
    }
    handleSendOtp();
  };

  const handleSendOtp = async () => {
    setIsLoading((prev) => ({ ...prev, sendingOtp: true }));

    try {
      const _data = {
        country_code: "+91",
        mobile_number: formData.phone,
      };

      await axios.post(`${BASE_URL}consumerAppAppRoute/sent-otp-by-web`, _data);

      setOtpDialog({
        open: true,
        otp: "",
        verifyOtp: true,
      });
    } catch (error) {
      console.error("Error sending OTP:", error);
      setSnackbar({
        open: true,
        message: "Failed to send OTP. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, sendingOtp: false }));
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpDialog.otp || otpDialog.otp.length !== 4) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 4-digit OTP",
        severity: "error",
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, verifyingOtp: true }));

    try {
      const _data = {
        country_code: "+91",
        mobile_number: formData.phone,
        otp: parseInt(otpDialog.otp),
      };

      await axios.post(
        `${BASE_URL}consumerAppAppRoute/verify-otp-by-web`,
        _data
      );

      await handleCheckUserExist();
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setSnackbar({
        open: true,
        message: "Invalid OTP. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, verifyingOtp: false }));
    }
  };

  const handleCheckUserExist = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}consumerAppAppRoute/register-consumer-by-web`,
        {
          personal_details: {
            phone: {
              country_code: "+91",
              mobile_number: formData.phone,
            },
            name: formData.name,
          },
          address_details_permanent: {
            pincode: formData.pincode,
          },
        }
      );

      if (res?.data?.status === 200) {
        // User registered successfully, now submit the warranty
        setIsLoading((prev) => ({ ...prev, submitting: true }));
        await onSubmit(file);
        setOtpDialog({
          open: false,
          otp: "",
          verifyOtp: false,
        });
      } else {
        throw new Error("Failed to register user");
      }
    } catch (error) {
      console.error("Error checking user existence:", error);
      setSnackbar({
        open: true,
        message: "Failed to register user. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Card className="shadow-lg">
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Details Section */}
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="bg-green-100 p-2 rounded-lg mr-3">
                  <Person className="text-green-600" />
                </div>
                <Typography variant="subtitle1" className="text-gray-600">
                  Personal Details
                </Typography>
              </div>
              <Divider className="my-2" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <TextField
                  name="name"
                  label="Full Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <Person className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Phone Number */}
                <TextField
                  name="phone"
                  label="Mobile Number"
                  type="tel"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <Phone className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                  value={formData.phone}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const digitsOnly = rawValue.replace(/\D/g, "");
                    if (digitsOnly.length <= 10) {
                      setFormData((prev) => ({
                        ...prev,
                        phone: digitsOnly,
                      }));
                    }
                  }}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="bg-orange-100 p-2 rounded-lg mr-3">
                  <LocationOn className="text-orange-600" />
                </div>
                <Typography variant="subtitle1" className="text-gray-600">
                  Address Details
                </Typography>
              </div>
              <Divider className="my-2" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pincode */}
                <TextField
                  name="pincode"
                  label="Pincode"
                  fullWidth
                  required
                  value={formData.pincode}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <LocationOn className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* City */}
                <TextField
                  name="city"
                  label="City"
                  fullWidth
                  required
                  value={formData.city}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <LocationCity className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* State */}
                <TextField
                  name="state"
                  label="State"
                  fullWidth
                  required
                  value={formData.state}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <LocationOn className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Country */}
                <TextField
                  name="country"
                  label="Country"
                  fullWidth
                  disabled
                  value={formData.country}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" className="ml-2">
                        <Public className="text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>

            {/* File Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <Person className="text-blue-600" />
                </div>
                <Typography variant="subtitle1" className="text-gray-600">
                  Warranty Document
                </Typography>
              </div>
              <Divider className="my-2" />

              <Box>
                <Button variant="outlined" component="label" sx={{ mr: 2 }}>
                  Upload Warranty Document
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </Button>
                {file && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      Selected file: {file.name}
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={handlePreview}
                      size="small"
                    >
                      <Visibility />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                variant="contained"
                fullWidth
                size="large"
                color="primary"
                type="submit"
                disabled={isLoading.sendingOtp || isLoading.submitting || !file}
                className="py-3 text-lg"
              >
                {isLoading.sendingOtp || isLoading.submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Register Warranty"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* OTP Dialog */}
      <Dialog
        open={otpDialog.open}
        onClose={() => setOtpDialog({ ...otpDialog, open: false })}
        PaperProps={{
          className: "rounded-2xl p-4 max-w-md",
        }}
      >
        <DialogTitle className="flex justify-between items-center pb-2">
          <Typography variant="h6" fontWeight="bold">
            Verify OTP
          </Typography>
          <IconButton
            onClick={() => setOtpDialog({ ...otpDialog, open: false })}
            className="absolute right-4 top-4"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="py-2">
          <Typography variant="body1" className="mb-4">
            We've sent an OTP to your mobile number{" "}
            <strong>+91 {formData.phone}</strong>
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            margin="normal"
            label="Enter 4-digit OTP"
            value={otpDialog.otp}
            onChange={(e) =>
              setOtpDialog({ ...otpDialog, otp: e.target.value })
            }
            inputProps={{
              maxLength: 4,
              inputMode: "numeric",
            }}
            className="mt-6"
          />
        </DialogContent>
        <DialogActions className="justify-between px-6 pb-4">
          <Button
            onClick={() => {
              if (canResendOtp) {
                setResendTimer(60);
                setCanResendOtp(false);
                handleSendOtp();
              }
            }}
            color="primary"
            disabled={!canResendOtp}
            className="min-w-[120px]"
          >
            Resend OTP {!canResendOtp && `(${resendTimer}s)`}
          </Button>
          <Button
            onClick={handleVerifyOtp}
            color="primary"
            variant="contained"
            disabled={isLoading.verifyingOtp}
            className="min-w-[120px]"
          >
            {isLoading.verifyingOtp ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Verify & Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Document Preview</Typography>
            <IconButton onClick={handleClosePreview}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {file &&
            (file.type === "application/pdf" ? (
              <iframe
                src={URL.createObjectURL(file)}
                width="100%"
                height="500px"
                title="Document Preview"
              />
            ) : (
              <img
                src={URL.createObjectURL(file)}
                alt="Document Preview"
                style={{ width: "100%", height: "auto" }}
              />
            ))}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WarrantyRegistrationForm;
